import React,{useState,useEffect} from 'react';
import {useParams,useNavigate} from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Badge from '../components/common/Badge';
import {Select} from '../components/common/FormField';
import api from '../services/api';
import {fmtCurrency} from '../utils/helpers';
import {openPrintWindow} from '../utils/printUtils';

const STATUS_CSS:Record<string,string>={UNPAID:'bg-red-100 text-red-700',PARTIAL:'bg-amber-100 text-amber-700',PAID:'bg-green-100 text-green-700',CANCELLED:'bg-slate-100 text-slate-500',PACKAGE:'bg-purple-100 text-purple-700'};

export default function BillDetailPage(){
  const {id}=useParams<{id:string}>();
  const nav=useNavigate();
  const [billDetail,setBillDetail]=useState<any>(null);
  const [detailTab,setDetailTab]=useState<'items'|'pay'|'refund'>('items');
  const [receipts,setReceipts]=useState<any[]>([]);
  const [providers,setProviders]=useState<any[]>([]);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const defaultPay={cashAmount:0,upiAmount:0,cardAmount:0,neftAmount:0,chequeAmount:0,corporateAmount:0,insuranceAmount:0,providerId:'',corporateClaimNumber:'',cardLast4:'',upiRef:'',chequeNumber:'',bankName:'',referenceNumber:'',remarks:''};
  const [payForm,setPayForm]=useState<any>({...defaultPay});
  const payTotal=()=>(['cashAmount','upiAmount','cardAmount','neftAmount','chequeAmount','corporateAmount','insuranceAmount'] as const).reduce((s,k)=>s+(+payForm[k]||0),0);
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const loadReceipts=async(billId:string)=>{
    try{const r=await api.getReceipts(billId).catch(()=>({data:[]}));setReceipts(Array.isArray(r?.data)?r.data:(r?.data?.data||r?.data?.items||r?.data?.rows||r?.data?.bills||r?.data?.records||[]));}catch{}
  };

  const loadBill=async()=>{
    if(!id)return;
    setLoading(true);
    try{
      const r=await api.getBillById(id).catch(()=>({data:null}));
      setBillDetail(r?.data||null);
      await loadReceipts(id);
      const pr=await api.getInsuranceProviders?.().catch(()=>({data:[]}));
      setProviders(pr?.data||[]);
    }catch{}
    setLoading(false);
  };

  useEffect(()=>{loadBill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[id]);

  const processPayment=async()=>{
    if(!billDetail||payTotal()<=0){notify('Enter payment amount');return;}
    setSaving(true);
    try{
      await api.recordPayment?.(billDetail.id,{...payForm,amount:payTotal(),paymentMethod:payTotal()>0?(payForm.cashAmount>0?'CASH':payForm.upiAmount>0?'UPI':payForm.cardAmount>0?'CARD':payForm.neftAmount>0?'NEFT':payForm.chequeAmount>0?'CHEQUE':payForm.corporateAmount>0?'CORPORATE':'INSURANCE'):'CASH'});
      notify('✅ Payment recorded!');setPayForm({...defaultPay});
      await loadReceipts(billDetail.id);
      setBillDetail((prev:any)=>({...prev,status:payTotal()>=(+prev.balance_amount||0)?'PAID':'PARTIAL'}));
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const fmtDate2=(d:string)=>d?new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'—';

  if(loading){
    return <Layout title="Bill Detail"><div className="p-12 text-center text-slate-400">Loading bill...</div></Layout>;
  }
  const printBill=async()=>{
    if(!billDetail)return;
    const b=billDetail;
    const patient=`${b.patient_first_name||b.firstName||''} ${b.patient_last_name||b.lastName||''}`.trim()||'—';
    const uhid=b.uhid||'—';
    const items=(b.items||b.bill_items||[]);
    const rows=items.map((i:any)=>`
      <tr><td>${i.item_description||i.itemDescription||i.item_name||'—'}</td>
      <td>${i.item_category||i.itemCategory||'—'}</td>
      <td style="text-align:center;">${i.quantity||1}</td>
      <td style="text-align:right;">₹${Number(i.unit_price||i.unitPrice||0).toLocaleString('en-IN')}</td>
      <td style="text-align:right;">${i.discount_percentage||i.discountPercentage||0}%</td>
      <td style="text-align:right;font-weight:700;">₹${Number(i.total_price||i.totalPrice||0).toLocaleString('en-IN')}</td></tr>`).join('');
    const html=`
      <div class="section">
        <div class="row">
          <div class="field"><div class="lbl">Patient</div><div class="val">${patient}</div></div>
          <div class="field"><div class="lbl">UHID</div><div class="val">${uhid}</div></div>
          <div class="field"><div class="lbl">Bill No.</div><div class="val">${b.bill_number||b.billNumber||'—'}</div></div>
          <div class="field"><div class="lbl">Date</div><div class="val">${new Date(b.bill_date||b.billDate||b.created_at||'').toLocaleDateString('en-IN')}</div></div>
        </div>
        <div class="row">
          <div class="field"><div class="lbl">Bill Type</div><div class="val">${b.bill_type||b.billType||'—'}</div></div>
          <div class="field"><div class="lbl">Status</div><div class="val">${b.status||'—'}</div></div>
          ${b.provider_name?`<div class="field"><div class="lbl">Payer</div><div class="val">${b.provider_name}</div></div>`:''}
        </div>
      </div>
      <table>
        <thead><tr><th>Description</th><th>Category</th><th>Qty</th><th>Rate</th><th>Disc%</th><th>Amount</th></tr></thead>
        <tbody>${rows||'<tr><td colspan="6" style="text-align:center;color:#888;">No items</td></tr>'}</tbody>
      </table>
      <div style="display:flex;justify-content:flex-end;margin-top:12px;">
        <table className="w-full text-sm min-w-max" style="width:260px;border:1px solid #ddd;border-radius:6px;overflow:hidden;">
          <tr><td class="label">Subtotal</td><td style="text-align:right;">₹${Number(b.total_amount||b.totalAmount||0).toLocaleString('en-IN')}</td></tr>
          <tr><td class="label">Discount</td><td style="text-align:right;color:#dc2626;">−₹${Number(b.discount_amount||b.discountAmount||0).toLocaleString('en-IN')}</td></tr>
          <tr><td class="label">GST</td><td style="text-align:right;">₹${Number(b.gst_amount||b.gstAmount||0).toLocaleString('en-IN')}</td></tr>
          <tr style="background:#f0fdf4;"><td class="label" style="font-size:13px;">Net Amount</td><td style="text-align:right;font-size:13px;font-weight:900;color:#15803d;">₹${Number(b.net_amount||b.netAmount||b.total_amount||0).toLocaleString('en-IN')}</td></tr>
          <tr><td class="label">Paid</td><td style="text-align:right;color:#2563eb;">₹${Number(b.paid_amount||b.paidAmount||0).toLocaleString('en-IN')}</td></tr>
          <tr style="background:#fff7ed;"><td class="label">Balance Due</td><td style="text-align:right;font-weight:700;color:#dc2626;">₹${Number(b.balance_amount||b.balanceAmount||0).toLocaleString('en-IN')}</td></tr>
        </table>
      </div>
    `;
    await openPrintWindow(html,'Tax Invoice / Bill');
  };

  if(!billDetail){
    return <Layout title="Bill Detail"><div className="p-12 text-center text-slate-400">Bill not found.<br/><button onClick={()=>nav('/billing')} className="btn-secondary mt-4">← Back to Billing</button></div></Layout>;
  }

  return(
    <Layout title={`Bill ${billDetail?.bill_number||billDetail?.billNumber||''}`} actions={
      <div className="flex gap-2">
        <button onClick={printBill} className="btn-secondary text-sm">🖨️ Print Bill</button>
        <button onClick={()=>nav('/billing')} className="btn-secondary text-sm">← Back to Billing</button>
      </div>
    }>
      {msg&&<div className="fixed top-4 right-4 bg-white border shadow-xl rounded-xl px-4 py-3 text-sm z-50">{msg}</div>}
      <div className="max-w-4xl mx-auto">
        {billDetail&&(
          <div>
            {/* Patient info */}
            <div className="bg-slate-50 rounded-xl p-3 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div><p className="text-xs text-slate-400">Patient</p><p className="font-semibold">{billDetail.patient_first_name} {billDetail.patient_last_name}</p><p className="text-xs font-mono text-[#02357A]">{billDetail.uhid}</p></div>
              <div><p className="text-xs text-slate-400">Type / Date</p><p className="font-semibold">{billDetail.bill_type} — {fmtDate2(billDetail.bill_date||billDetail.billDate)}</p></div>
              <div className="text-right">
                <p className="text-xs text-slate-400">Balance</p>
                <p className={`text-xl font-bold ${(+billDetail.balance_amount||0)>0?'text-red-600':'text-green-600'}`}>₹{fmtCurrency(billDetail.balance_amount||billDetail.balanceAmount||0)}</p>
                <Badge label={billDetail.status||'UNPAID'} className={STATUS_CSS[billDetail.status]}/>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-4 bg-slate-100 rounded-xl p-1">
              {(['items','pay','refund'] as const).map(t=>(
                <button key={t} onClick={()=>setDetailTab(t)} className={`flex-1 text-sm py-1.5 rounded-lg capitalize ${detailTab===t?'bg-white shadow font-semibold text-[#02357A]':'text-slate-500'}`}>{t==='items'?'📋 Line Items':t==='pay'?'💳 Payment':' Refund'}</button>
              ))}
            </div>

            {detailTab==='items'&&(
              <div>
                {(billDetail.provider_id||billDetail.providerId)&&(
                  <div className="mb-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-xs font-semibold text-[#02357A] mb-2">🏢 Corporate / Insurance Billing</p>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="text-center bg-white rounded p-2"><p className="text-xs text-slate-400">Total</p><p className="font-bold">₹{fmtCurrency(billDetail.net_amount||billDetail.netAmount||0)}</p></div>
                      <div className="text-center bg-green-50 rounded p-2"><p className="text-xs text-green-600">Corporate Pays</p><p className="font-bold text-green-700">₹{fmtCurrency(billDetail.corporate_amount||billDetail.corporateAmount||0)}</p></div>
                      <div className="text-center bg-amber-50 rounded p-2"><p className="text-xs text-amber-600">Patient Pays</p><p className="font-bold text-amber-700">₹{fmtCurrency(billDetail.patient_share||billDetail.patientShare||billDetail.balance_amount||0)}</p></div>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50"><tr>{['Description','Code','Qty','Price','Discount','GST','Total','Corp Share','Patient Share'].map(h=><th key={h} className="p-2 text-left text-slate-500 whitespace-nowrap">{h}</th>)}</tr></thead>
                    <tbody className="divide-y">
                      {(billDetail.items||[]).map((it:any)=>{
                        const total=parseFloat(it.totalPrice||it.total_price||0);
                        const corpPct=parseFloat(billDetail.corporate_share_pct||100);
                        const patPct=100-corpPct;
                        return(
                          <tr key={it.id} className="hover:bg-slate-50">
                            <td className="p-2">{it.itemDescription||it.item_description}</td>
                            <td className="p-2 font-mono text-slate-400">{it.itemCode||it.item_code}</td>
                            <td className="p-2 text-center">{it.quantity}</td>
                            <td className="p-2">₹{parseFloat(it.unitPrice||it.unit_price||0).toFixed(2)}</td>
                            <td className="p-2 text-red-500">{parseFloat(it.discountAmount||0)>0?`₹${parseFloat(it.discountAmount).toFixed(2)}`:'—'}</td>
                            <td className="p-2 text-slate-400">{(parseFloat(it.cgstAmount||0)+parseFloat(it.sgstAmount||0))>0?`₹${(parseFloat(it.cgstAmount||0)+parseFloat(it.sgstAmount||0)).toFixed(2)}`:'—'}</td>
                            <td className="p-2 font-semibold">₹{total.toFixed(2)}</td>
                            <td className="p-2 text-green-600">{corpPct>0&&corpPct<100?`₹${(total*corpPct/100).toFixed(2)}`:'—'}</td>
                            <td className="p-2 text-amber-700">{patPct>0&&patPct<100?`₹${(total*patPct/100).toFixed(2)}`:corpPct===100?'—':`₹${total.toFixed(2)}`}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="border-t-2 bg-slate-50 font-semibold text-xs">
                      <tr>
                        <td colSpan={6} className="p-2 text-right">TOTAL</td>
                        <td className="p-2">₹{parseFloat(billDetail.total_amount||billDetail.totalAmount||0).toFixed(2)}</td>
                        <td className="p-2 text-green-600">₹{parseFloat(billDetail.corporate_amount||billDetail.corporateAmount||0).toFixed(2)}</td>
                        <td className="p-2 text-amber-700">₹{parseFloat(billDetail.patient_share||billDetail.patientShare||billDetail.balance_amount||0).toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {detailTab==='pay'&&(
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50 rounded-xl p-3 text-sm">
                  <div className="text-center"><p className="text-xs text-slate-400">Net Amount</p><p className="font-bold">₹{fmtCurrency(billDetail.net_amount||billDetail.netAmount||0)}</p></div>
                  <div className="text-center"><p className="text-xs text-slate-400">Already Paid</p><p className="font-bold text-green-600">₹{fmtCurrency(billDetail.paid_amount||billDetail.paidAmount||0)}</p></div>
                  <div className="text-center"><p className="text-xs text-slate-400">Balance Due</p><p className="font-bold text-red-600">₹{fmtCurrency(billDetail.balance_amount||billDetail.balanceAmount||0)}</p></div>
                </div>
                <p className="text-xs font-bold text-slate-500 uppercase">💰 Split Payment — Enter amounts for each mode</p>
                <div className="space-y-2">
                  {([
                    {f:'cashAmount',ic:'💵',lb:'Cash',rf:'',rl:''},
                    {f:'upiAmount',ic:'📱',lb:'UPI / GPay',rf:'upiRef',rl:'UPI Reference'},
                    {f:'cardAmount',ic:'💳',lb:'Debit / Credit Card',rf:'cardLast4',rl:'Card Last 4'},
                    {f:'neftAmount',ic:'🔄',lb:'NEFT / RTGS',rf:'referenceNumber',rl:'Transaction Ref'},
                    {f:'chequeAmount',ic:'🏦',lb:'Cheque / DD',rf:'chequeNumber',rl:'Cheque No'},
                    {f:'corporateAmount',ic:'🏢',lb:'Corporate / TPA',rf:'',rl:''},
                  ] as any[]).map(mode=>{
                    const val=+(payForm[mode.f]||0);
                    const balance=parseFloat(billDetail.balance_amount||billDetail.balanceAmount||0);
                    const otherTotal=(['cashAmount','upiAmount','cardAmount','neftAmount','chequeAmount','corporateAmount'] as const).filter(k=>k!==mode.f).reduce((s,k)=>s+(+(payForm[k]||0)),0);
                    const remaining=Math.max(0,balance-otherTotal);
                    return(
                      <div key={mode.f} className={`rounded-xl border-2 overflow-hidden ${val>0?'border-blue-400':'border-slate-200'}`}>
                        <div className="flex items-center gap-2 p-2.5 bg-white">
                          <span>{mode.ic}</span>
                          <span className="flex-1 text-sm font-medium">{mode.lb}</span>
                          {val===0&&remaining>0&&<div className="flex gap-1">
                            {[500,1000,2000].filter(v=>v<=remaining+1).map(v=>(
                              <button key={v} type="button" onClick={()=>setPayForm({...payForm,[mode.f]:Math.min(v,remaining)})} className="text-xs bg-slate-100 hover:bg-blue-50 px-1.5 py-1 rounded">₹{v>=1000?v/1000+'K':v}</button>
                            ))}
                            <button type="button" onClick={()=>setPayForm({...payForm,[mode.f]:remaining})} className="text-xs bg-blue-100 text-[#02357A] px-2 py-1 rounded font-semibold">Full</button>
                          </div>}
                          <div className="flex items-center gap-1">
                            <span className="text-slate-400 text-xs">₹</span>
                            <input type="number" min="0" step="0.01" placeholder="0" className={`w-24 text-right font-bold text-sm px-2 py-1.5 border-2 rounded-lg outline-none ${val>0?'border-blue-400 text-[#02357A]':'border-slate-200'}`} value={val||''} onChange={e=>setPayForm({...payForm,[mode.f]:+e.target.value})}/>
                            {val>0&&<button type="button" onClick={()=>setPayForm({...payForm,[mode.f]:0})} className="text-slate-400 hover:text-red-500">×</button>}
                          </div>
                        </div>
                        {val>0&&mode.rf&&<div className="px-3 pb-2 bg-slate-50 border-t pt-1.5"><label className="text-xs text-slate-400">{mode.rl}</label><input className="input w-full text-xs mt-0.5" value={payForm[mode.rf]||''} onChange={e=>setPayForm({...payForm,[mode.rf]:e.target.value})}/></div>}
                        {val>0&&mode.f==='corporateAmount'&&<div className="px-3 pb-2 bg-slate-50 border-t pt-1.5 grid grid-cols-2 gap-2"><div><label className="text-xs text-slate-400">Provider</label><Select value={payForm.providerId} onChange={e=>setPayForm({...payForm,providerId:e.target.value})} className="w-full text-xs"><option value="">Select...</option>{providers.map((p:any)=><option key={p.id} value={p.id}>{p.provider_name||p.providerName}</option>)}</Select></div><div><label className="text-xs text-slate-400">Claim No</label><input className="input w-full text-xs" value={payForm.corporateClaimNumber||''} onChange={e=>setPayForm({...payForm,corporateClaimNumber:e.target.value})}/></div></div>}
                      </div>
                    );
                  })}
                </div>
                {payTotal()>0&&(()=>{
                  const net=parseFloat(billDetail.balance_amount||billDetail.balanceAmount||0);
                  const total=payTotal();
                  const change=total-net;
                  return<div className="bg-slate-800 rounded-xl p-3 text-white text-sm space-y-1"><div className="flex justify-between font-bold"><span>Collecting</span><span className="text-green-400">₹{fmtCurrency(total)}</span></div>{change>0.01&&<div className="flex justify-between text-amber-300 text-xs"><span>Change to Return</span><span>₹{fmtCurrency(change)}</span></div>}{total<net-0.01&&<div className="flex justify-between text-red-300 text-xs"><span>Balance Remaining</span><span>₹{fmtCurrency(net-total)}</span></div>}</div>;
                })()}
                <div className="flex gap-2">
                  <button onClick={processPayment} disabled={saving} className="btn-primary flex-1 py-3">{saving?'Processing...':'💳 Collect & Generate Receipt'}</button>
                  <button onClick={()=>setPayForm({...defaultPay})} className="btn-secondary px-4">Clear</button>
                </div>
                {receipts.length>0&&<div>{receipts.map((r:any)=><div key={r.id} className="flex justify-between items-center text-xs p-2 bg-slate-50 rounded mb-1"><span className="font-mono text-[#02357A]">{r.receipt_number||r.receiptNumber}</span><span className="text-green-600 font-semibold">₹{fmtCurrency(r.amount_received||r.amountReceived||0)}</span><button onClick={async()=>await openPrintWindow(document.getElementById('printArea')?.innerHTML||'<p>Receipt</p>','Receipt')} className="text-[#02357A] hover:underline">🖨️</button></div>)}</div>}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
